import { useState, useEffect, useCallback } from "react";
import type { DayPlan } from "@/types";
import { dbGet, dbSet } from "@/lib/db";
import { planKey, todayKey } from "@/lib/constants";

export function usePlans() {
  const [plans, setPlans] = useState<DayPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(todayKey);

  const reload = useCallback((date: string) => {
    dbGet<DayPlan[]>(planKey(date)).then((p) => {
      setPlans(p ?? []);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    reload(currentDate);
  }, [currentDate, reload]);

  const persist = useCallback(
    (next: DayPlan[]) => {
      setPlans(next);
      dbSet(planKey(currentDate), next);
    },
    [currentDate]
  );

  const addPlan = useCallback(
    (plan: DayPlan) => {
      persist([...plans, plan]);
    },
    [plans, persist]
  );

  const togglePlan = useCallback(
    (doctorId: string) => {
      persist(plans.map((p) => (p.doctorId === doctorId ? { ...p, done: !p.done } : p)));
    },
    [plans, persist]
  );

  const removePlan = useCallback(
    (doctorId: string) => {
      persist(plans.filter((p) => p.doctorId !== doctorId));
    },
    [plans, persist]
  );

  const markDone = useCallback(
    (doctorId: string) => {
      // Mark done on today's plan regardless of currentDate
      const todayStr = todayKey();
      if (currentDate === todayStr) {
        persist(plans.map((p) => (p.doctorId === doctorId ? { ...p, done: true } : p)));
      } else {
        // Operate on today's plan in the background
        dbGet<DayPlan[]>(planKey(todayStr)).then((todayPlans) => {
          if (todayPlans) {
            const updated = todayPlans.map((p) => (p.doctorId === doctorId ? { ...p, done: true } : p));
            dbSet(planKey(todayStr), updated);
          }
        });
      }
    },
    [plans, persist, currentDate]
  );

  const loadDate = useCallback((date: string) => {
    setCurrentDate(date);
    setLoading(true);
  }, []);

  // Operate on an arbitrary date without changing the viewed date
  const addPlanForDate = useCallback(
    async (date: string, plan: DayPlan) => {
      const existing = (await dbGet<DayPlan[]>(planKey(date))) ?? [];
      const next = [...existing, plan];
      await dbSet(planKey(date), next);
      if (date === currentDate) setPlans(next);
    },
    [currentDate]
  );

  const togglePlanForDate = useCallback(
    async (date: string, doctorId: string) => {
      const existing = (await dbGet<DayPlan[]>(planKey(date))) ?? [];
      const next = existing.map((p) => (p.doctorId === doctorId ? { ...p, done: !p.done } : p));
      await dbSet(planKey(date), next);
      if (date === currentDate) setPlans(next);
    },
    [currentDate]
  );

  const removePlanForDate = useCallback(
    async (date: string, doctorId: string) => {
      const existing = (await dbGet<DayPlan[]>(planKey(date))) ?? [];
      const next = existing.filter((p) => p.doctorId !== doctorId);
      await dbSet(planKey(date), next);
      if (date === currentDate) setPlans(next);
    },
    [currentDate]
  );

  const getPlansForDate = useCallback(
    async (date: string): Promise<DayPlan[]> => {
      if (date === currentDate) return plans;
      return (await dbGet<DayPlan[]>(planKey(date))) ?? [];
    },
    [currentDate, plans]
  );

  return {
    plans,
    loading,
    currentDate,
    addPlan,
    togglePlan,
    removePlan,
    markDone,
    loadDate,
    addPlanForDate,
    togglePlanForDate,
    removePlanForDate,
    getPlansForDate,
  };
}
